import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { Interpolation } from '../../config/i18n/components/Interpolation';
import { OrganizationProfilesService } from '../../organization/profiles/services/OrganizationProfilesService';
import { ButtonBox, DangerButton, TertiaryButton } from '../components/Buttons';

type Props = {
  profileId: number;
  profileName: string;
  onClosed: (profileId: number | undefined) => void;
};

export const DeleteProfileFlow = ({ profileId, profileName, onClosed }: Props) => {
  const [profileService] = useState(new OrganizationProfilesService());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      profileService.cancelAllRequests();
    };
  }, [profileService]);

  const [name, nameComponent] = useFormItem({
    type: InputType.TYPE_TEXT,
    label: t('deleteProfile.name.input'),
    value: '',
    validator: null,
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      focusedLabel: t('deleteProfile.name.focused'),
      showRequiredIndicator: true,
    },
  });

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await profileService.deleteProfile(profileId);
      onClosed(profileId);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  const canSubmit = !loading && name === profileName;

  return (
    <Dialog title={t('deleteProfile.title')} onDismiss={() => onClosed(undefined)}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          <Interpolation i18nKey="deleteProfile.content" items={[{ name: 'color_gray', color: 'grey.700' }]} values={{ profileName }} />
          {nameComponent}
          <ButtonBox>
            <TertiaryButton disabled={loading} onClick={() => onClosed(undefined)}>
              {t('deleteProfile.back')}
            </TertiaryButton>
            <DangerButton disabled={!canSubmit} type="submit">
              {t('deleteProfile.submit')}
            </DangerButton>
          </ButtonBox>
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
